import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { supabase } from '../lib/supabaseClient';
import { 
  mapSupabaseStoryToStory, 
  mapSupabaseMediaArticleToMediaArticle,
  type Story,
  type MediaArticle
} from '../types/supabase';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { useToast } from '../hooks/use-toast';
import { GlowWrapper } from '../components/ui/glow-wrapper';
import { motion } from 'framer-motion';
import { Navbar } from '../components/navbar';

const VoicesPage = () => {
  // State for data
  const [featuredStories, setFeaturedStories] = useState<Story[]>([]);
  const [recentStories, setRecentStories] = useState<Story[]>([]);
  const [popularStories, setPopularStories] = useState<Story[]>([]);
  const [mediaArticles, setMediaArticles] = useState<MediaArticle[]>([]);
  
  // State for loading and error handling
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const fetchVoicesData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        console.log('Supabase client initialized:', !!supabase);
        console.log('Starting data fetch from Supabase...');
        
        // Fetch featured stories
        const { data: featuredStoriesData, error: featuredStoriesError } = await supabase
          .from('stories')
          .select('*')
          .eq('approved', true)
          .eq('featured', true)
          .order('created_at', { ascending: false });
        
        console.log('Featured stories query executed, error?', !!featuredStoriesError);
          
        if (featuredStoriesError) throw featuredStoriesError;
        
        // Fetch recent stories
        const { data: recentStoriesData, error: recentStoriesError } = await supabase
          .from('stories')
          .select('*')
          .eq('approved', true)
          .order('created_at', { ascending: false })
          .limit(6);
          
        console.log('Recent stories query executed, error?', !!recentStoriesError);
          
        if (recentStoriesError) throw recentStoriesError;
        
        // Fetch popular stories
        const { data: popularStoriesData, error: popularStoriesError } = await supabase
          .from('stories')
          .select('*')
          .eq('approved', true)
          .order('read_count', { ascending: false })
          .limit(6);
          
        if (popularStoriesError) throw popularStoriesError;
        
        // Fetch media articles
        const { data: mediaArticlesData, error: mediaArticlesError } = await supabase
          .from('media_articles')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (mediaArticlesError) throw mediaArticlesError;
        
        // Debug logging for data retrieval
        console.log('Featured stories data:', featuredStoriesData);
        console.log('Recent stories data:', recentStoriesData);
        console.log('Popular stories data:', popularStoriesData);
        console.log('Media articles data:', mediaArticlesData);
        
        // Map the data to our client-side types with null checking
        const mappedFeatured = featuredStoriesData?.map(mapSupabaseStoryToStory) || [];
        const mappedRecent = recentStoriesData?.map(mapSupabaseStoryToStory) || [];
        const mappedPopular = popularStoriesData?.map(mapSupabaseStoryToStory) || [];
        const mappedMedia = mediaArticlesData?.map(mapSupabaseMediaArticleToMediaArticle) || [];
        
        console.log('Mapped featured stories:', mappedFeatured);
        console.log('Mapped recent stories:', mappedRecent);
        console.log('Mapped popular stories:', mappedPopular);
        
        setFeaturedStories(mappedFeatured);
        setRecentStories(mappedRecent);
        setPopularStories(mappedPopular);
        setMediaArticles(mappedMedia);
      } catch (err) {
        console.error('Error fetching voices data:', err);
        console.error('Error details:', JSON.stringify(err));
        setError('Failed to load content. Please try again later.');
        toast({
          title: 'Error',
          description: 'Failed to load content. Please try again later.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchVoicesData();
  }, [toast]);

  // Increment read count when a story is opened
  const handleStoryClick = async (story: Story) => {
    try {
      console.log('Updating read count for story:', story.id);
      // Update read count in Supabase
      const { error } = await supabase
        .from('stories')
        .update({ read_count: (story.read_count || 0) + 1 })
        .eq('id', story.id);
      
      if (error) {
        console.error('Error updating read count:', error);
      } else {
        console.log('Read count updated successfully');
      }
      
      // Navigate to story page
      setLocation(`/story-direct/${story.id}`);
    } catch (err) {
      console.error('Error updating read count:', err);
    }
  };

  // Loading skeletons for cards
  const renderSkeletons = (count: number = 3) => {
    return Array(count).fill(0).map((_, i) => (
      <Card key={`skeleton-${i}`} className="w-full bg-teal-200 text-teal-800 rounded-xl border border-teal-200 dark:bg-teal-900/20 dark:border-teal-700 dark:text-white">
        <CardHeader>
          <Skeleton className="h-6 w-3/4 mb-2" />
          <Skeleton className="h-4 w-1/2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
        <CardFooter>
          <Skeleton className="h-9 w-24" />
        </CardFooter>
      </Card>
    ));
  };

  // Render a story card
  const renderStoryCard = (story: Story) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 20
      }}
      className="relative group w-full"
      key={story.id}
    >
      {/* Glow effect that shows on hover */}
      <div className="absolute -inset-0.5 rounded-xl bg-gradient-to-r dark:from-teal-900 dark:to-teal-800 opacity-0 group-hover:opacity-50 blur transition-all duration-500 group-hover:duration-200"></div>
      <Card 
        className="w-full h-full cursor-pointer transition-all duration-300 bg-white dark:bg-black dark:hover:bg-teal-500/10 rounded-xl border border-teal-200 dark:text-white justify-between items-stretch relative group-hover:border-teal-400 hover:shadow-[0_0_15px_rgba(45,212,191,0.3)]"
        onClick={() => handleStoryClick(story)}
      >
        <CardHeader>
          <CardTitle>{story.title}</CardTitle>
          <CardDescription className="text-teal-800 dark:text-teal-200">
            {story.author_name && `By ${story.author_name}`}
            {story.read_time && ` • ${story.read_time} read`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="line-clamp-3">{story.excerpt || (story.content ? story.content.substring(0, 150) : '')}...</p>
        </CardContent>
        <CardFooter className="flex justify-between">
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button variant="default" className="text-black" size="sm">
              Read More
            </Button>
          </motion.div>
          {story.featured && <Badge variant="default" className="text-black animate-pulse-slow">Featured</Badge>}
        </CardFooter>
      </Card>
    </motion.div>
  );

  // Render a media article card
  const renderMediaArticleCard = (article: MediaArticle) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 20
      }}
      className="relative group w-full"
      key={article.id}
    >
      {/* Glow effect that shows on hover */}
      <div className="absolute -inset-0.5 rounded-xl bg-gradient-to-r dark:from-black dark:to-teal-800 opacity-0 group-hover:opacity-50 blur transition-all duration-500 group-hover:duration-200"></div>
      <Card 
        className="w-full h-full bg-white dark:bg-black hover:bg-teal-500/10 border border-teal-200 rounded-xl text-black dark:text-white justify-between items-stretch relative group-hover:border-teal-400 hover:shadow-[0_0_15px_rgba(45,212,191,0.3)] transition-all duration-300"
      >
        <CardHeader>
          <CardTitle>{article.name}</CardTitle>
          {article.tagline && <CardDescription className="text-teal-800 dark:text-teal-200">{article.tagline}</CardDescription>}
        </CardHeader>
        <CardContent>
          {article.quotes && <p className="italic">{article.quotes}</p>}
        </CardContent>
        <CardFooter className="flex justify-between">
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button variant="default" className="text-black" onClick={() => article.link && window.open(article.link, '_blank')}>
              Read Article
            </Button>
          </motion.div>
          {article.featured && <Badge variant="default" className="text-black animate-pulse-slow">Featured</Badge>}
        </CardFooter>
      </Card>
    </motion.div>
  );

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 bg-white dark:bg-black">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4">Error</h2>
          <p className="text-black dark:text-white">{error}</p>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 15 }}
          >
            <GlowWrapper color="teal" intensity="high">
              <Button variant="default" size="lg" onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </GlowWrapper>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 bg-white dark:bg-black">
      <Navbar/>
      <div className="text-center mb-12 animate-rise mt-16">
        <h1 className="text-6xl font-bold mb-4 animate-float text-black dark:text-white">Voices of <span className="text-[#14b8a6] animate-shimmer">Elevita</span></h1>
        <p className="text-lg italic text-gray-900 dark:text-gray-200 max-w-2xl mx-auto my-4">
          Real stories from real people. Discover personal journeys, challenges, and triumphs.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 15 }}
          >
            <GlowWrapper color="teal" intensity="high">
              <Button variant="default" size="lg" className="text-black" onClick={() => setLocation('/submit-story')}>
                Share your story
              </Button>
            </GlowWrapper>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 15 }}
          >
            <Button variant="default" size="lg" className="text-black" onClick={() => setLocation('/')}>
              Head to homepage
            </Button>
          </motion.div>
        </div>
      </div>

      <div className="mb-16 animate-rise" style={{animationDelay: '0.1s'}}>
        <motion.h2 
          className="text-2xl font-bold mb-6 border-b text-black dark:text-white border-teal-400/30 pb-2 inline-block relative group"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 400, damping: 20 }}
        >
          <span className="relative z-10">Featured Stories</span>
          <span className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-teal-400 to-teal-200 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading
            ? renderSkeletons(3)
            : featuredStories.length > 0
              ? featuredStories.map(renderStoryCard)
              : (
                <div className="col-span-full text-center py-8 bg-white dark:bg-black">
                  <p className="text-gray-800 dark:text-gray-200">No featured stories available.</p>
                </div>
              )
          }
        </div>
      </div>

      <div className="mb-16 animate-rise" style={{animationDelay: '0.2s'}}>
        <Tabs defaultValue="recent">
          <div className="flex justify-between items-center mb-6">
            <motion.h2 
              className="text-2xl font-bold border-b border-teal-500 pb-2 inline-block relative group"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
              <span className="relative z-10">Stories</span>
              <span className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-teal-400 to-teal-200 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
            </motion.h2>
            <TabsList className="card-glass bg-teal-500/20 border border-teal-500 rounded-2xl">
              <TabsTrigger 
                value="recent" 
                className="text-black dark:text-white border border-teal-400 data-[state=active]:bg-teal-900/70 data-[state=active]:border-teal-300 data-[state=active]:text-white data-[state=active]:shadow-[0_0_10px_rgba(45,212,191,0.3)] transition-all duration-300 hover:bg-teal-900/50 rounded-l-xl"
              >
                Recent
              </TabsTrigger>
              <TabsTrigger 
                value="popular" 
                className="text-black dark:text-white border border-teal-400 data-[state=active]:bg-teal-900/70 data-[state=active]:border-teal-300 data-[state=active]:text-white data-[state=active]:shadow-[0_0_10px_rgba(45,212,191,0.3)] transition-all duration-300 hover:bg-teal-900/50 rounded-r-xl"
              >
                Popular
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="recent">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {isLoading
                ? renderSkeletons(6)
                : recentStories.length > 0
                  ? recentStories.map(renderStoryCard)
                  : (
                    <div className="col-span-full text-center py-8 bg-white dark:bg-black">
                      <p className="text-gray-800 dark:text-gray-200">No recent stories available.</p>
                    </div>
                  )
              }
            </div>
          </TabsContent>
          <TabsContent value="popular">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {isLoading
                ? renderSkeletons(6)
                : popularStories.length > 0
                  ? popularStories.map(renderStoryCard)
                  : (
                    <div className="col-span-full text-center py-8 bg-white dark:bg-black">
                      <p className="text-gray-800 dark:text-gray-200">No popular stories available.</p>
                    </div>
                  )
              }
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <div className="mb-16 animate-rise" style={{animationDelay: '0.3s'}}>
        <motion.h2 
          className="text-2xl font-bold mb-6 border-b border-teal-500 pb-2 inline-block relative group"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 400, damping: 20 }}
        >
          <span className="relative z-10">Media Amplified Voices</span>
          <span className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-teal-400 to-teal-200 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {isLoading
            ? renderSkeletons(4)
            : mediaArticles.length > 0
              ? mediaArticles.map(renderMediaArticleCard)
              : (
                <div className="col-span-full text-center py-8 bg-white dark:bg-black">
                  <p className="text-gray-800 dark:text-gray-200">No media articles available.</p>
                </div>
              )
          }
        </div>
      </div>

      <div className="mt-16 mb-12">
        <div className="max-w-3xl mx-auto card-glass backdrop-blur-md bg-white dark:bg-black border border-teal-200 rounded-xl p-8">
          <h3 className="text-xl font-bold mb-6 text-teal-800 dark:text-teal-200 text-center">Current Healthcare System Gaps</h3>
          <div className="relative">
            <motion.div 
              className="flex flex-col items-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <svg viewBox="0 0 500 250" className="w-full h-52">
                {/* Definition for glow filter */}
                <defs>
                  <filter id="glow-error">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>
                
                {/* Healthcare system components */}
                <motion.g
                  initial={{ opacity: 0.3 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                >
                  <rect x="30" y="50" width="80" height="50" fill="#14b8a6" opacity="0.7" rx="8" className="stroke-teal-200 fill-teal-200 dark:fill-teal-600 border border-teal-500" strokeWidth="1">
                    <title className="text-black dark:text-white">Patient Entry Point</title>
                  </rect>
                  <text x="70" y="80" textAnchor="middle" className="fill-black dark:fill-white" fontSize="12" fontWeight="bold">Patients</text>
                </motion.g>
                
                {/* The broken pathway */}
                <motion.g>
                  <motion.path 
                    d="M110,75 L170,75" 
                    stroke="#14b8a6" 
                    strokeWidth="2" 
                    fill="none"
                    initial={{ pathLength: 0, opacity: 0.3 }}
                    animate={{ pathLength: 1, opacity: 0.8 }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                  
                  <motion.path 
                    d="M170,75 L240,75" 
                    stroke="#E53E3E" 
                    strokeWidth="3" 
                    fill="none"
                    strokeDasharray="5 5"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 2, repeat: Infinity, repeatType: "loop" }}
                    filter="url(#glow-error)"
                  />
                  
                  <motion.circle
                    cx="170"
                    cy="75"
                    r="10"
                    fill="#E53E3E"
                    initial={{ scale: 0 }}
                    animate={{ scale: [0, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity, repeatType: "loop", delay: 1 }}
                    filter="url(#glow-error)"
                  >
                    <title className="fill-red-500">Gap in Care</title>
                  </motion.circle>
                  
                  <motion.text 
                    x="170" 
                    y="110" 
                    textAnchor="middle" 
                    className="fill-red-500" 
                    fontSize="12"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1 }}
                    filter="url(#glow-error)"
                  >Gap in Care</motion.text>
                </motion.g>
                
                {/* Healthcare system components continued */}
                <motion.g
                  initial={{ opacity: 0.5 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", delay: 0.5 }}
                >
                  <rect x="240" y="50" width="80" height="50" opacity="0.7" rx="8" className="stroke-teal-200 fill-teal-200 dark:fill-teal-600 border border-teal-500" strokeWidth="1">
                    <title className="fill-black dark:fill-white">Care Delivery</title>
                  </rect>
                  <text x="280" y="80" textAnchor="middle" className="fill-black dark:fill-white" fontSize="12" fontWeight="bold">Care</text>
                </motion.g>
                
                {/* Good pathway */}
                <motion.g>
                  <motion.path 
                    d="M320,75 L390,75" 
                    stroke="#14b8a6" 
                    strokeWidth="2" 
                    fill="none"
                    initial={{ pathLength: 0, opacity: 0.3 }}
                    animate={{ pathLength: 1, opacity: 0.8 }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                </motion.g>
                
                <motion.g
                  initial={{ opacity: 0.5 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", delay: 1 }}
                >
                  <rect x="390" y="50" width="80" height="50" opacity="0.7" rx="8" className="stroke-teal-200 fill-teal-200 dark:fill-teal-600 border border-teal-500" strokeWidth="1">
                    <title className="text-black dark:text-white">Better Outcomes</title>
                  </rect>
                  <text x="430" y="80" textAnchor="middle" className="fill-black dark:fill-white" fontSize="12" fontWeight="bold">Outcomes</text>
                </motion.g>
                
                {/* People falling through the cracks */}
                <motion.g>
                  <motion.circle 
                    cx="170" 
                    cy="75" 
                    r="5" 
                    className="fill-black dark:fill-white"
                    initial={{ cy: 75 }}
                    animate={{ cy: 200 }}
                    transition={{ duration: 3, repeat: Infinity, repeatDelay: 1, delay: 1 }}
                  />
                  <motion.circle 
                    cx="180" 
                    cy="75" 
                    r="5" 
                    className="fill-black dark:fill-white"
                    initial={{ cy: 75 }}
                    animate={{ cy: 200 }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 2, delay: 2 }}
                  />
                  <motion.circle 
                    cx="160" 
                    cy="75" 
                    r="5" 
                    className="fill-black dark:fill-white"
                    initial={{ cy: 75 }}
                    animate={{ cy: 200 }}
                    transition={{ duration: 4, repeat: Infinity, repeatDelay: 1, delay: 3 }}
                  />
                </motion.g>
                
                {/* Elevita solution */}
                <motion.path 
                  d="M110,75 Q170,170 240,75" 
                  stroke="#14b8a6" 
                  strokeWidth="3" 
                  fill="none"
                  strokeDasharray="3 3"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 0.8 }}
                  transition={{ duration: 3, delay: 2 }}
                />
                <motion.text 
                  x="170" 
                  y="145" 
                  textAnchor="middle" 
                  fill="#14b8a6" 
                  fontSize="12"
                  fontWeight="bold"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 3 }}
                >Elevita's Bridge</motion.text>
              </svg>
              <div className="text-center max-w-2xl mx-auto">
                <p className="text-teal-800 dark:text-teal-200 mb-2 font-bold">What This Visualizes:</p>
                <p className="mb-4">This diagram illustrates how traditional healthcare systems can have critical gaps where vulnerable patients fall through the cracks, and how Elevita aims to bridge those gaps.</p>
                <p className="text-gray-600 dark:text-[#E0E0E0] italic">"A system that can unintentionally fall short for those who need support most"</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="card-glass backdrop-blur-md bg-white dark:bg-black border border-teal-200 rounded-lg p-8 text-center animate-rise" style={{animationDelay: '0.4s'}}>
        <h2 className="text-2xl font-bold mb-4 animate-soft-pulse">Share Your Story</h2>
        <p className="mb-6">
          Your experience <span className="font-bold italic text-teal-800 dark:text-teal-200">really</span> matters. Share your story to inspire others and contribute to our community.
        </p>
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          transition={{ type: "spring", stiffness: 400, damping: 15 }}
        >
          <GlowWrapper color="teal" intensity="high">
            <Button variant="default" size="lg" className="text-black" onClick={() => setLocation('/submit-story')}>
              Submit Your Story
            </Button>
          </GlowWrapper>
        </motion.div>
      </div>
    </div>
  );
};

export default VoicesPage;